import type { SocialProviderSlug, User } from '@/interfaces/user.interface';
import { cxmService } from '@/services/cxm.service';
import { StorageSerializers, useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import type { MaybeRef } from 'vue';
import { useEcommerceStore } from '../modules/flokee/stores/ecommerce.store';
import { setLocaleByLanguageCode } from '@/plugins/i18n';
import { LoginProvider } from '@/interfaces/loginProvider.interface';
import type {
  CreateAccountDTO,
  CreateAccountOrderDTO,
  TiendanubeConnectResponse,
  TiendanubeInstallationResponse,
  UpdateAccountDTO,
  UpdateUserDTO,
} from '@/services/cxm.service.interface';
import { queryClient } from '@/plugins/vue-query';
import * as VueGtag from 'vue-gtag';
import { event } from 'vue-gtag';
import { isProduction } from '@/utils/environment.utils';
import { NotificationSocket } from '@/sockets/notification.socket';
import { useNoticeStore } from './notice.store';
import { useOrderSummaryStore } from './orderSummary.store';
import { getEffectiveBu, getLoginRouteNameForPath } from '@/utils/bu.utils';
import { BusinessUnitKey } from '@/modules/bu/registry.base';
import { useProjectStore } from '@/modules/cleepo/stores/project.store';
import router from '@/router';
import { isAdminRole } from '@/utils/role.utils';
import { handleAxiosError } from '@/utils/error.utils';

interface AuthState {
  access_token: MaybeRef<string | undefined>;
  login_provider: MaybeRef<LoginProvider | undefined>;
  user?: User;
  notificationSocket?: NotificationSocket;
  lastRoutePath: MaybeRef<string | undefined>;
  pending_tiendanube_installation: MaybeRef<TiendanubeInstallationResponse | undefined>;
}

const PENDING_TIENDANUBE_INSTALLATION_STORAGE_KEY = 'pending_tiendanube_installation';

function isTiendanubeConnectResponse(value: unknown): value is TiendanubeConnectResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const response = value as Partial<TiendanubeConnectResponse>;

  return Number.isInteger(response.store_id) && Number.isInteger(response.ecommerce_id);
}

function isTiendanubeInstallationResponse(value: unknown): value is TiendanubeInstallationResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const response = value as Partial<TiendanubeInstallationResponse>;

  return (
    typeof response.installation_token === 'string' &&
    response.installation_token.length > 0 &&
    typeof response.installation_expires_at === 'string'
  );
}

const pendingTiendanubeInstallationSerializer = {
  read(value: string): TiendanubeInstallationResponse {
    const installation = StorageSerializers.object.read(value);

    if (!isTiendanubeInstallationResponse(installation)) {
      throw new TypeError('Invalid pending Tiendanube installation storage value.');
    }

    return installation;
  },
  write(value: TiendanubeInstallationResponse | undefined): string {
    return value === undefined ? 'null' : StorageSerializers.object.write(value);
  },
};

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    access_token: useStorage('access_token', undefined, localStorage),
    login_provider: useStorage('login_provider', undefined, localStorage),
    user: undefined,
    notificationSocket: undefined,
    lastRoutePath: useStorage('last_route_path', undefined, localStorage),
    pending_tiendanube_installation: useStorage<TiendanubeInstallationResponse | undefined>(
      PENDING_TIENDANUBE_INSTALLATION_STORAGE_KEY,
      undefined,
      sessionStorage,
      {
        serializer: pendingTiendanubeInstallationSerializer,
        onError: () => sessionStorage.removeItem(PENDING_TIENDANUBE_INSTALLATION_STORAGE_KEY),
      },
    ),
  }),
  getters: {
    currentAccount: (state) => {
      return state.user?.current_account;
    },
    currentOrder: (state) => {
      return state.user?.current_order;
    },
    isAdmin: (state) => {
      return isAdminRole(state.user);
    },
  },
  actions: {
    async init() {
      this.subscribeToNotificationSocket();
      const ecommerceStore = useEcommerceStore();
      const projectStore = useProjectStore();
      const orderSummaryStore = useOrderSummaryStore();

      // Always init the order summary first — it provides the BU context.
      await orderSummaryStore.init();

      // Determine BU from the order summary (data-driven).
      const order_bu = orderSummaryStore.orderSummary?.business_unit;
      const resolved_bu = getEffectiveBu({
        path_value: window.location.pathname,
        origin_value: window.location.origin,
        use_stored: true,
      });
      const effective_bu = order_bu ?? resolved_bu;

      // Only initialize the relevant product store based on the order's BU.
      const storeInitPromises = [
        effective_bu === BusinessUnitKey.Flokee ? ecommerceStore.init() : Promise.resolve(),
        effective_bu === BusinessUnitKey.Cleepo ? projectStore.init() : Promise.resolve(),
      ];
      await Promise.all(storeInitPromises);

      if (isProduction()) {
        //Set gtag user id — derive reference from whichever store was initialized.
        const reference_id = ecommerceStore.currentEcommerce?.id ?? projectStore.currentProject?.id;

        VueGtag.set({
          user_id: `user_${this.user?.id}-order_${this.user?.current_order_id}-reference_${reference_id}-app_${effective_bu ?? 'UNKNOWN'}`,
        });
      }

      //check for notices and show them
      const noticeStore = useNoticeStore();
      noticeStore.checkNotices();
    },
    async register(register_data: {
      name: string;
      email: string;
      password: string;
      timezone: string;
      language_id: number;
    }) {
      await cxmService.register(register_data);

      await this.login(register_data.email, register_data.password);

      // Track sign_up event after successful registration and login
      if (isProduction() && this.user) {
        event('sign_up', {
          method: 'email', // Default method for email/password registration
          user_id: this.user.id,
          account_id: this.user.current_account_id,
          order_id: this.user.current_order_id,
          // Additional context for better analytics
          signup_method: 'email_password',
          user_email: register_data.email,
          user_name: register_data.name,
          timezone: register_data.timezone,
          language_id: register_data.language_id,
        });
      }
    },
    async login(email: string, password: string) {
      const { data: response } = await cxmService.login(email, password);

      const { data } = response;

      this.access_token = data.access_token;
      this.login_provider = LoginProvider.Cxm;

      await this.invalidate();
      await this.getCurrentUser();

      // Track login event after successful authentication
      if (isProduction() && this.user) {
        event('login', {
          method: 'email', // Login method used
          user_id: this.user.id,
          account_id: this.user.current_account_id,
          order_id: this.user.current_order_id,
          // Additional context for better analytics
          login_method: 'email_password',
          user_email: email,
        });
      }
    },
    async createTiendanubeInstallation(code: string, state?: string) {
      const { data: response } = await cxmService.createTiendanubeInstallation(code, state);
      const installation = response?.data;

      if (!isTiendanubeInstallationResponse(installation)) {
        throw new Error('CXM did not preserve the Tiendanube installation. Please try again.');
      }

      this.pending_tiendanube_installation = installation;

      return installation;
    },
    getPendingTiendanubeInstallation() {
      return this.pending_tiendanube_installation;
    },
    clearPendingTiendanubeInstallation() {
      this.pending_tiendanube_installation = undefined;
    },
    getPostAuthenticationRouteName(defaultRouteName: string): string {
      if (!this.getPendingTiendanubeInstallation()) {
        return defaultRouteName;
      }

      return this.user?.current_account_id && this.user?.current_order_id
        ? 'WizardSelectEcommerceProviderView'
        : 'WizardCreateAccountView';
    },
    async completeTiendanubeInstallation() {
      const installation = this.getPendingTiendanubeInstallation();
      if (!installation) {
        throw new Error('There is no pending Tiendanube installation to complete.');
      }

      try {
        const { data: response } = await cxmService.completeTiendanubeInstallation(
          installation.installation_token,
        );
        const connection = response?.data;

        if (!isTiendanubeConnectResponse(connection)) {
          throw new Error('CXM did not confirm the Tiendanube connection. Please try again.');
        }

        this.clearPendingTiendanubeInstallation();

        return connection;
      } catch (error) {
        const { status } = handleAxiosError(error);
        if (status === '403' || status === '404' || status === '410') {
          this.clearPendingTiendanubeInstallation();
        }

        throw error;
      }
    },
    async loginWithGoogle(token: string) {
      const { data: response } = await cxmService.googleLogin(token);

      const { data } = response;

      this.access_token = data.access_token;
      this.login_provider = LoginProvider.Google;

      await this.invalidate();
      await this.getCurrentUser();

      // Track login event after successful authentication
      if (isProduction() && this.user) {
        event('login', {
          method: 'google', // Login method used
          user_id: this.user.id,
          account_id: this.user.current_account_id,
          order_id: this.user.current_order_id,
          // Additional context for better analytics
          login_method: 'google_oauth',
        });
      }
    },
    async loginWithGoogleOneTap(credential: string) {
      const { data: response } = await cxmService.googleOneTapLogin(credential);

      const { data } = response;

      this.access_token = data.access_token;
      this.login_provider = LoginProvider.Google;

      await this.invalidate();
      await this.getCurrentUser();

      // Track login event after successful authentication
      if (isProduction() && this.user) {
        event('login', {
          method: 'google', // Login method used
          user_id: this.user.id,
          account_id: this.user.current_account_id,
          order_id: this.user.current_order_id,
          // Additional context for better analytics
          login_method: 'google_one_tap',
        });
      }
    },
    async getCurrentUser() {
      const { data } = await queryClient.ensureQueryData({
        queryKey: ['current-user'],
        meta: { persist: true },
        queryFn: async () => {
          const request = await cxmService.getCurrentUser();
          queryClient.setQueryData(['current-user'], request);
          return request;
        },
      });

      this.user = data.data;

      setLocaleByLanguageCode(this.user.language.code);

      await this.init();
    },
    async setCurrentUserPassword(data: { password: string; password_confirmation: string }) {
      if (!this.user?.id) throw new Error('User is not set.');
      await cxmService.setPassword(this.user?.id, data);
    },
    async deleteCurrentUserSocialProvider(provider: SocialProviderSlug) {
      if (!this.user?.id) throw new Error('User is not set.');
      await cxmService.deleteUserSocialProvider(this.user.id, provider);
      await queryClient.removeQueries({
        queryKey: ['current-user'],
      });
      await this.getCurrentUser();
    },
    async updateCurrentUser(data: Partial<UpdateUserDTO>) {
      if (!this.user?.id) throw new Error('User is not set.');
      await cxmService.updateUser(this.user?.id, data);
      await queryClient.removeQueries({
        queryKey: ['current-user'],
      });
      await this.getCurrentUser();
    },
    async logout() {
      // Resolve BU from route/domain BEFORE clearing state — this doesn't depend on auth.
      const login_route_name = getLoginRouteNameForPath({
        path_value: router.currentRoute.value.path,
        origin_value: window.location.origin,
        use_stored: true,
      });

      await cxmService.logout();

      localStorage.clear();

      this.notificationSocket?.close();
      this.notificationSocket = undefined;
      this.access_token = undefined;
      this.login_provider = undefined;
      this.user = undefined;

      await this.invalidate();

      if (isProduction()) {
        VueGtag.set({ user_id: '' });
      }
      router.push({ name: login_route_name });
    },
    async createOrder(data: CreateAccountOrderDTO) {
      if (!this.user?.id) throw new Error('User is not set.');
      await cxmService.createOrder(data);
      await this.invalidate();
      await this.getCurrentUser();
    },
    async createAccount(data: CreateAccountDTO) {
      if (!this.user?.id) throw new Error('User is not set.');
      await cxmService.createAccount(data);
      await this.invalidate();
      await this.getCurrentUser();
    },
    async setCurrentAccount(account_id: number) {
      if (!this.user?.id) throw new Error('User is not set.');
      await cxmService.setCurrentAccount(this.user?.id, account_id);

      await this.invalidate();
      await this.getCurrentUser();
    },
    async updateCurrentAccount(data: Partial<UpdateAccountDTO>) {
      if (!this.currentAccount?.id) throw new Error('Account is not set.');
      await cxmService.updateAccount(this.currentAccount?.id, data);
      await queryClient.removeQueries({
        queryKey: ['current-user'],
      });
      await this.getCurrentUser();
    },
    async setCurrentOrder(order_id: number) {
      if (!this.user?.id) throw new Error('User is not set.');
      await cxmService.setCurrentOrder(this.user?.id, order_id);
      await queryClient.removeQueries({
        queryKey: ['current-user'],
      });
      await this.getCurrentUser();
    },
    subscribeToNotificationSocket() {
      if (!this.user?.id || this.notificationSocket) return;
      this.notificationSocket = new NotificationSocket(this.user.id);
    },
    async invalidate() {
      const ecommerceStore = useEcommerceStore();
      const projectStore = useProjectStore();
      const orderSummaryStore = useOrderSummaryStore();
      await ecommerceStore.clear();
      await projectStore.clear();
      // The order names the user's product. Left behind, the next session on
      // this tab renders the previous user's contacts on shared routes.
      await orderSummaryStore.clear();
      await queryClient.removeQueries({
        queryKey: ['current-user'],
      });
    },
  },
});
